import {
    IRailingGeometry,
    IRoomGeometry,
    IStairCalculationResult,
    IStairConfig,
    IStairGeometry,
    IStepGeometry,
    IStringerGeometry,
    IValidationItem,
} from '@stair-craft/common/util';

import { RISER_BOARD_THICKNESS, STRINGER_THICKNESS, STRINGER_WIDTH } from '../constants';
import { IStairNorms, RESIDENTIAL_NORMS } from '../norms';
import { validate, validateRailing, withHeadroomHint } from '../validator';
import { evenPitchRailing } from './shared-baluster-count/baluster-count';
import { IHeadroomResult, calculateHeadroom, lowHeadroomSteps } from './shared-calculate-headroom/calculate-headroom';
import { shareJunctionPosts } from './shared-junction-posts/junction-posts';
import { IPlanPoint } from './shared-plan-geometry/plan-geometry';
import { capRailingPosts } from './shared-post-caps/post-caps';
import { bringPostsToFloor } from './shared-posts-to-floor/posts-to-floor';
import { addRailingPlanks } from './shared-railing-planks/railing-planks';
import { railingVolume } from './shared-railing-wood/railing-wood';
import { settleStairRailings } from './shared-settle-railings/settle-railings';
import { stairPlacement } from './shared-stair-placement/stair-placement';
import { IStairWoodEstimate, stairWoodEstimate } from './shared-stair-wood/stair-wood';
import { generateRailings } from './straight-generate-railings/generate-railings';
import { generateSteps } from './straight-generate-steps/generate-steps';
import { generateStringers } from './straight-generate-stringers/generate-stringers';
import { generateUpperFloorRailing } from './straight-generate-upper-floor-railing/generate-upper-floor-railing';

/** Inclusive integer range [from, to] (empty when to < from). */
function rangeInclusive(from: number, to: number): number[] {
    const indices: number[] = [];
    for (let i: number = from; i <= to; i++) {
        indices.push(i);
    }
    return indices;
}

/**
 * Step count and (riser, tread) dimensions for a straight stair, coupled to whichever
 * field the user pinned (stepCount / riserHeight / stairAngle) with norm fallbacks.
 * Extracted from `calculateStraight` to keep its complexity low.
 */
function resolveStepDimensions(
    config: IStairConfig,
    norms: IStairNorms,
    totalRise: number
): { stepCount: number; riserHeight: number; treadDepth: number } {
    let stepCount: number;
    if (config.stepCount != null) {
        stepCount = config.stepCount;
    } else if (config.riserHeight != null) {
        stepCount = Math.round(totalRise / config.riserHeight);
    } else {
        stepCount = Math.round(totalRise / norms.riserHeightIdeal);
    }

    // Ensure at least 3 steps
    stepCount = Math.max(3, stepCount);

    let riserHeight: number = config.riserHeight ?? totalRise / stepCount;

    let treadDepth: number;
    if (config.stairAngle != null) {
        treadDepth = config.treadDepth ?? riserHeight / Math.tan(config.stairAngle * (Math.PI / 180));
    } else {
        treadDepth = config.treadDepth ?? norms.blondelIdeal - 2 * riserHeight;
        // Clamp tread depth to reasonable range only when angle is not set
        treadDepth = Math.max(norms.treadDepthMin, Math.min(norms.treadDepthMax, treadDepth));
    }

    // Recalculate step count if riserHeight was forced
    if (config.riserHeight != null) {
        stepCount = Math.round(totalRise / riserHeight);
        riserHeight = totalRise / stepCount;
    } else {
        riserHeight = totalRise / stepCount;
    }

    return { stepCount, riserHeight, treadDepth };
}

/**
 * Indices of steps to highlight red: treads with too little headroom, steps poking outside
 * the room in X or Z, or insufficient entry clearance before the stair. Extracted from
 * `calculateStraight` to keep its complexity low.
 */
function computeWarningStepIndices(
    config: IStairConfig,
    placement: IPlanPoint,
    treadDepth: number,
    lowHeadroom: readonly number[],
    stepCount: number
): number[] {
    const warningSet: Set<number> = new Set<number>(lowHeadroom);
    const lastIdx: number = stepCount - 2;

    // Room bounds check: stair position in room coords
    const stairX: number = placement.x;
    const stairZ: number = placement.z;

    // X bounds: stair occupies stairX..stairX+stairWidth
    if (stairX < 0 || stairX + config.stairWidth > config.roomWidth) {
        rangeInclusive(0, lastIdx).forEach((i: number): void => {
            warningSet.add(i);
        });
    }

    // Z bounds: each step at stairZ + i*treadDepth; highlight all outside room
    rangeInclusive(0, lastIdx).forEach((i: number): void => {
        const stepZ: number = stairZ + i * treadDepth;
        if (stepZ < 0 || stepZ + treadDepth > config.roomLength) {
            warningSet.add(i);
        }
    });

    // Entry clearance: insufficient space to approach the stair
    if (stairZ >= 0 && stairZ < config.stairWidth) {
        const lastBadStep: number = Math.min(lastIdx, Math.ceil((config.stairWidth - stairZ) / treadDepth));
        rangeInclusive(0, lastBadStep).forEach((i: number): void => {
            warningSet.add(i);
        });
    }

    return [...warningSet];
}

/**
 * Calculate a straight staircase from the given config.
 */
export function calculateStraight(config: IStairConfig): IStairCalculationResult {
    const norms: IStairNorms = RESIDENTIAL_NORMS;

    // floorHeight = floor-to-floor (includes slab thickness)
    // Stair rises the full floorHeight to reach the upper floor surface
    const totalRise: number = config.floorHeight + config.floorThickness;

    // ─── Step count & dimensions ───────────────────────────
    const { stepCount, riserHeight, treadDepth } = resolveStepDimensions(config, norms, totalRise);

    // ─── Derived values ────────────────────────────────────
    const totalRun: number = (stepCount - 1) * treadDepth;
    const stairAngle: number = Math.atan2(totalRise, totalRun) * (180 / Math.PI);
    const totalMarchLength: number = Math.sqrt(totalRise ** 2 + totalRun ** 2);

    const blondelValue: number = 2 * riserHeight + treadDepth;

    // ─── Geometry ──────────────────────────────────────────
    const steps: IStepGeometry[] = generateSteps(stepCount, riserHeight, treadDepth, config);
    const placement: IPlanPoint = stairPlacement(config, config, totalRun);
    // Headroom: from the nosing line up to the slab around the opening
    const headroomResult: IHeadroomResult = calculateHeadroom(steps, config, placement);
    const headroom: number = headroomResult.headroom;
    const stringers: IStringerGeometry[] = generateStringers(steps, config, riserHeight);
    // One baluster pitch on the flight and the level runs: the going cut into gaps within the norm.
    const railed: IStairConfig = { ...config, railing: evenPitchRailing(config.railing, treadDepth) };
    const stairRailings: IRailingGeometry[] = config.railing.enabled ? generateRailings(steps, railed) : [];
    const upperRailings: IRailingGeometry[] =
        config.railing.enabled && config.railing.upperEnabled ? generateUpperFloorRailing(railed) : [];
    settleStairRailings(stairRailings, steps, config.stringerType, config.railing.balusterSpacing);
    bringPostsToFloor(stairRailings, steps, stringers);
    shareJunctionPosts(stairRailings, upperRailings, placement, config.railing.balusterSpacing);
    const railings: IRailingGeometry[] = [...stairRailings, ...upperRailings];
    addRailingPlanks(railings, stringers, steps, config, placement);
    capRailingPosts(railings);

    const geometry: IStairGeometry = {
        steps,
        stringers,
        railings,
        landings: [],
        risers: config.risersEnabled,
        boundingBox: {
            min: { x: 0, y: 0, z: 0 },
            max: {
                x: config.stairWidth,
                y: totalRise,
                z: totalRun + treadDepth,
            },
        },
    };

    // ─── Material estimation ───────────────────────────────
    // Steps: treads + risers (stepCount-1 treads, stepCount risers)
    const treadVolumePerStep: number = (config.stairWidth / 1000) * (treadDepth / 1000) * (config.treadThickness / 1000);
    const riserBoardHeight: number = riserHeight - config.treadThickness;
    const riserVolumePerStep: number = (config.stairWidth / 1000) * (riserBoardHeight / 1000) * (RISER_BOARD_THICKNESS / 1000);
    const treadVolume: number = (stepCount - 1) * treadVolumePerStep;
    const riserVolume: number = stepCount * riserVolumePerStep;

    // Stringers
    const stringerCount: number = 2;
    const stringerLength: number = totalMarchLength / 1000;
    const stringerVolume: number = stringerCount * stringerLength * (STRINGER_THICKNESS / 1000) * (STRINGER_WIDTH / 1000);

    // Every part by its own species; the railing's balusters, posts and handrail included
    const { woodVolumeM3, estimatedWeightKg, estimatedCostUsd }: IStairWoodEstimate = stairWoodEstimate(
        { tread: treadVolume, riser: riserVolume, stringer: stringerVolume, railing: railingVolume(railings, config.railing) },
        config
    );

    // ─── Room geometry ─────────────────────────────────────
    const room: IRoomGeometry = {
        roomLength: config.roomLength,
        roomWidth: config.roomWidth,
        floorHeight: config.floorHeight,
        floorThickness: config.floorThickness,
        openingLength: config.openingLength,
        openingWidth: config.openingWidth,
        openingX: config.openingX,
        openingZ: config.openingZ,
    };

    // ─── Validation ────────────────────────────────────────
    const validation: IValidationItem[] = withHeadroomHint(
        validate(riserHeight, treadDepth, stairAngle, headroom, config.stairWidth),
        false
    );
    validation.push(...validateRailing(config.railing));
    const isValid: boolean = validation.every((v: IValidationItem) => v.severity !== 'error');

    // ─── Step warnings (red highlight) ─────────────────────
    const warningStepIndices: number[] = computeWarningStepIndices(
        config,
        placement,
        treadDepth,
        lowHeadroomSteps(headroomResult, norms.headroomMin),
        stepCount
    );

    return {
        riserHeight: Math.round(riserHeight * 10) / 10,
        treadDepth: Math.round(treadDepth * 10) / 10,
        stairAngle: Math.round(stairAngle),
        totalRun: Math.round(totalRun),
        headroom: Math.round(headroom),
        blondelValue: Math.round(blondelValue),
        totalMarchLength: Math.round(totalMarchLength),
        woodVolumeM3: Math.round(woodVolumeM3 * 1000) / 1000,
        estimatedWeightKg: Math.round(estimatedWeightKg),
        estimatedCostUsd: Math.round(estimatedCostUsd),
        placementValid: warningStepIndices.length === 0,
        stepCount,
        room,
        geometry,
        validation,
        isValid,
        warningStepIndices,
    };
}
